import { computed, ref } from "vue";
import type {
  WsStreamId,
  WsServerMessage,
  WsSnapshotMessage,
  WsEventMessage,
  WsActionAckMessage,
  WsActionErrorMessage,
  WsActionKind,
  WsActionPayloadByKind
} from "@idle-rpg/shared";

type ConnectionState = "idle" | "connecting" | "connected" | "reconnecting" | "degraded";

type SnapshotHandler<TStream extends WsStreamId> = (payload: WsSnapshotMessage<TStream>) => void;
type EventHandler<TStream extends WsStreamId> = (payload: WsEventMessage<TStream>) => void;

type TimeoutHandle = ReturnType<typeof setTimeout>;

function setTimeoutCompat(fn: () => void, delayMs: number): TimeoutHandle {
  const w = (globalThis as unknown as { window?: unknown }).window as { setTimeout?: unknown } | undefined;
  if (w && typeof w.setTimeout === "function") {
    return (w.setTimeout as (fn: () => void, delayMs: number) => TimeoutHandle)(fn, delayMs);
  }
  return globalThis.setTimeout(fn, delayMs) as unknown as TimeoutHandle;
}

function clearTimeoutCompat(id: TimeoutHandle) {
  const w = (globalThis as unknown as { window?: unknown }).window as { clearTimeout?: unknown } | undefined;
  if (w && typeof w.clearTimeout === "function") {
    (w.clearTimeout as (id: TimeoutHandle) => void)(id);
    return;
  }
  (globalThis.clearTimeout as (id: TimeoutHandle) => void)(id);
}

type PendingSnapshotRequest<TStream extends WsStreamId = WsStreamId> = {
  requestId: string;
  resolve: (msg: WsSnapshotMessage<TStream>) => void;
  reject: (err: Error) => void;
  timeoutId: TimeoutHandle | null;
};

type PendingActionRequest = {
  clientRequestId: string;
  kind: WsActionKind;
  resolve: (msg: WsActionAckMessage) => void;
  reject: (err: Error) => void;
  timeoutId: TimeoutHandle | null;
};

const socketRef = ref<WebSocket | null>(null);
const connectionState = ref<ConnectionState>("idle");
const lastError = ref<string | null>(null);
const lastMessageAt = ref<number | null>(null);
const subscribedStreams = new Set<WsStreamId>();
const snapshotHandlers = new Map<WsStreamId, Set<SnapshotHandler<WsStreamId>>>();
const eventHandlers = new Map<WsStreamId, Set<EventHandler<WsStreamId>>>();
const pendingSnapshots = new Map<WsStreamId, Map<string, PendingSnapshotRequest>>();
const pendingActions = new Map<string, PendingActionRequest>();
type PendingResync = { stream: WsStreamId; lastSeq?: number; params?: Record<string, unknown>; requestId?: string };
const pendingResyncs: PendingResync[] = [];
const lastSeqByStream = new Map<WsStreamId, number>();
const epochByStream = new Map<WsStreamId, number>();
const intentionalClose = new WeakSet<WebSocket>();

let currentToken: string | null = null;
let currentCharacterId: string | null = null;
let retryTimer: TimeoutHandle | null = null;
let retryCount = 0;
let blockedToken: string | null = null;
let blockedUntilMs: number | null = null;

function buildWsUrl({ characterId }: { characterId: string }) {
  const metaEnv = (import.meta as { env?: { VITE_API_BASE?: string; PROD?: boolean } }).env;

  // Match the REST API client logic: use VITE_API_BASE if set, otherwise:
  // - In production (PROD=true): use empty string (same-origin)
  // - In development (PROD=false): use http://localhost:8787
  const apiBase = metaEnv?.VITE_API_BASE ?? (metaEnv?.PROD ? "" : "http://localhost:8787");

  // If apiBase is empty (production same-origin), derive from window.location
  const base = (() => {
    if (apiBase) return apiBase;

    // Production mode with no explicit base: use same-origin
    const hasWindow = typeof window !== "undefined";
    if (hasWindow && window.location?.origin) {
      return window.location.origin;
    }

    // Fallback for non-browser contexts
    return "http://127.0.0.1:8787";
  })();

  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("characterId", characterId);
  return url.toString();
}

function clearRetry() {
  if (retryTimer !== null) {
    clearTimeoutCompat(retryTimer);
    retryTimer = null;
  }
}

function clearPendingSnapshots(errorMessage: string) {
  for (const [stream, pending] of pendingSnapshots.entries()) {
    for (const request of pending.values()) {
      if (request.timeoutId !== null) {
        clearTimeoutCompat(request.timeoutId);
      }
      request.reject(new Error(errorMessage));
    }
    pending.clear();
    pendingSnapshots.delete(stream);
  }
}

function clearPendingActions(errorMessage: string) {
  for (const pending of pendingActions.values()) {
    if (pending.timeoutId !== null) {
      clearTimeoutCompat(pending.timeoutId);
    }
    pending.reject(new Error(errorMessage));
  }
  pendingActions.clear();
}

function clearPendingSnapshotsForStream(stream: WsStreamId, errorMessage: string, requestId?: string) {
  const pending = pendingSnapshots.get(stream);
  if (!pending) return;

  if (requestId) {
    const request = pending.get(requestId);
    if (!request) return;
    if (request.timeoutId !== null) {
      clearTimeoutCompat(request.timeoutId);
    }
    request.reject(new Error(errorMessage));
    pending.delete(requestId);
    if (pending.size === 0) pendingSnapshots.delete(stream);
    return;
  }

  for (const request of pending.values()) {
    if (request.timeoutId !== null) {
      clearTimeoutCompat(request.timeoutId);
    }
    request.reject(new Error(errorMessage));
  }
  pending.clear();
  pendingSnapshots.delete(stream);
}

function createActionError(payload: WsActionErrorMessage) {
  const err = new Error(payload.message);
  (err as Error & { code?: string; kind?: WsActionKind; clientRequestId?: string }).code = payload.code;
  (err as Error & { code?: string; kind?: WsActionKind; clientRequestId?: string }).kind = payload.kind;
  (err as Error & { code?: string; kind?: WsActionKind; clientRequestId?: string }).clientRequestId = payload.clientRequestId;
  return err;
}

function resetSessionTransientState(reason: string) {
  lastSeqByStream.clear();
  epochByStream.clear();
  pendingResyncs.splice(0, pendingResyncs.length);
  clearPendingSnapshots(reason);
  clearPendingActions(reason);
}

function scheduleReconnect() {
  clearRetry();
  if (blockedToken && blockedUntilMs && Date.now() < blockedUntilMs && currentToken === blockedToken) {
    return;
  }
  retryCount += 1;
  const delay = Math.min(12000, 500 * Math.pow(1.6, retryCount));
  retryTimer = setTimeoutCompat(() => {
    if (currentToken && currentCharacterId) {
      void connect({ token: currentToken, characterId: currentCharacterId });
    }
  }, delay);
}

function attachSocket(ws: WebSocket) {
  ws.addEventListener("open", () => {
    connectionState.value = "connected";
    retryCount = 0;
    const pending = pendingResyncs.splice(0, pendingResyncs.length);
    for (const payload of pending) {
      send({
        type: "resync",
        stream: payload.stream,
        lastSeq: payload.lastSeq,
        params: payload.params,
        requestId: payload.requestId
      });
    }
    send({ type: "subscribe", streams: Array.from(subscribedStreams) });
  });

  ws.addEventListener("message", (event) => {
    lastMessageAt.value = Date.now();
    let parsed: WsServerMessage | null = null;
    try {
      parsed = JSON.parse(typeof event.data === "string" ? event.data : event.data.toString()) as WsServerMessage;
    } catch (err) {
      lastError.value = err instanceof Error ? err.message : String(err);
      return;
    }

    if (parsed.type === "snapshot") {
      const epoch = typeof (parsed as { epoch?: unknown }).epoch === "number" ? (parsed as { epoch: number }).epoch : 0;
      const prevEpoch = epochByStream.get(parsed.stream);
      if (prevEpoch === undefined || prevEpoch !== epoch) {
        // DO restart/hibernation can reset server seq; epoch change tells us to reset per-stream tracking.
        epochByStream.set(parsed.stream, epoch);
      }
      // Snapshots are authoritative; do not trigger resync solely due to snapshot seq discontinuity.
      lastSeqByStream.set(parsed.stream, parsed.seq);
      const handlers = snapshotHandlers.get(parsed.stream);
      handlers?.forEach((handler) => handler(parsed));
      const pending = pendingSnapshots.get(parsed.stream);
      if (pending) {
        // Prefer requestId-based correlation when present (supports concurrent requests on the same stream).
        const reqId = (parsed as { requestId?: unknown }).requestId;
        if (typeof reqId === "string" && reqId.length > 0) {
          const request = pending.get(reqId);
          if (request) {
            if (request.timeoutId !== null) {
              clearTimeoutCompat(request.timeoutId);
            }
            request.resolve(parsed);
            pending.delete(reqId);
          }
          if (pending.size === 0) {
            pendingSnapshots.delete(parsed.stream);
          }
        }
      }
      return;
    }

    if (parsed.type === "event") {
      const lastSeq = lastSeqByStream.get(parsed.stream);
      const epoch = typeof (parsed as { epoch?: unknown }).epoch === "number" ? (parsed as { epoch: number }).epoch : 0;
      const prevEpoch = epochByStream.get(parsed.stream);
      if (prevEpoch === undefined || prevEpoch !== epoch) {
        epochByStream.set(parsed.stream, epoch);
        lastSeqByStream.set(parsed.stream, parsed.seq);
      } else if (lastSeq !== undefined) {
        if (parsed.seq <= lastSeq) {
          // Ignore duplicates/out-of-order events to avoid resync loops.
          return;
        }
        if (parsed.seq > lastSeq + 1) {
          resync(parsed.stream, lastSeq);
        }
        lastSeqByStream.set(parsed.stream, parsed.seq);
      } else {
        lastSeqByStream.set(parsed.stream, parsed.seq);
      }
      const handlers = eventHandlers.get(parsed.stream);
      handlers?.forEach((handler) => handler(parsed));
      return;
    }

    if (parsed.type === "action_ack" || parsed.type === "action_error") {
      const clientRequestId = (parsed as { clientRequestId?: unknown }).clientRequestId;
      if (typeof clientRequestId !== "string" || clientRequestId.length === 0) return;
      const pending = pendingActions.get(clientRequestId);
      if (!pending) return;
      if (pending.timeoutId !== null) {
        clearTimeoutCompat(pending.timeoutId);
      }
      pendingActions.delete(clientRequestId);

      if (parsed.type === "action_error") {
        pending.reject(createActionError(parsed));
        return;
      }

      if (pending.kind !== parsed.kind) {
        pending.reject(new Error("Action response kind mismatch"));
        return;
      }

      pending.resolve(parsed);
      return;
    }

    if (parsed.type === "error") {
      lastError.value = parsed.message;
      if (parsed.stream) {
        clearPendingSnapshotsForStream(parsed.stream, parsed.message, parsed.requestId);
      }
      if (parsed.code === "session_already_connected" && currentToken) {
        blockedToken = currentToken;
        blockedUntilMs = Date.now() + 30_000;
        clearRetry();
        connectionState.value = "degraded";
      }
    }
  });

  ws.addEventListener("close", () => {
    if (socketRef.value !== ws) return;
    socketRef.value = null;
    if (intentionalClose.has(ws)) {
      intentionalClose.delete(ws);
      return;
    }
    clearPendingSnapshots("WebSocket disconnected");
    clearPendingActions("WebSocket disconnected");
    if (currentToken && blockedToken && blockedUntilMs && Date.now() < blockedUntilMs && currentToken === blockedToken) {
      connectionState.value = "degraded";
      return;
    }
    connectionState.value = currentToken ? "reconnecting" : "idle";
    scheduleReconnect();
  });

  ws.addEventListener("error", () => {
    if (socketRef.value !== ws) return;
    lastError.value = "WebSocket error";
    connectionState.value = "degraded";
  });
}

export async function connect({ token, characterId }: { token: string; characterId: string }) {
  if (blockedToken === token && blockedUntilMs && Date.now() < blockedUntilMs) {
    connectionState.value = "degraded";
    lastError.value = "This account session is already connected in another client.";
    return;
  }

  if (socketRef.value && currentToken === token) {
    if (currentCharacterId === characterId) return;

    // Same session token, different character: keep the socket and ask the server to swap context.
    if (socketRef.value.readyState === WebSocket.OPEN) {
      currentCharacterId = characterId;
      resetSessionTransientState("Character changed");
      send({ type: "set_character", characterId });
      return;
    }

    // If we're still connecting/reconnecting, prefer reopening with the new characterId (can't reliably swap yet).
    currentCharacterId = characterId;
    resetSessionTransientState("Character changed");
  }

  const switchingSession = currentToken !== null && currentToken !== token;
  if (switchingSession) {
    resetSessionTransientState("Session changed");
  }

  if (socketRef.value) {
    intentionalClose.add(socketRef.value);
    socketRef.value.close();
    socketRef.value = null;
  }

  currentToken = token;
  currentCharacterId = characterId;
  clearRetry();
  connectionState.value = "connecting";

  let ws: WebSocket;
  try {
    // Auth uses Sec-WebSocket-Protocol so the token isn't leaked via URL/query logs.
    ws = new WebSocket(buildWsUrl({ characterId }), [`idle-rpg-session.${token}`]);
  } catch (err) {
    lastError.value = err instanceof Error ? err.message : String(err);
    connectionState.value = "degraded";
    scheduleReconnect();
    return;
  }
  socketRef.value = ws;
  attachSocket(ws);
}

export function disconnect() {
  clearRetry();
  currentToken = null;
  currentCharacterId = null;
  blockedToken = null;
  blockedUntilMs = null;
  resetSessionTransientState("Session changed");
  if (socketRef.value) {
    intentionalClose.add(socketRef.value);
  }
  socketRef.value?.close();
  socketRef.value = null;
  connectionState.value = "idle";
}

function send(payload: unknown) {
  const ws = socketRef.value;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify(payload));
}

export function subscribe(streams: WsStreamId[]) {
  for (const stream of streams) {
    subscribedStreams.add(stream);
  }
  if (connectionState.value === "connected") {
    send({ type: "subscribe", streams });
  }
}

export function unsubscribe(streams: WsStreamId[]) {
  for (const stream of streams) {
    subscribedStreams.delete(stream);
  }
  if (connectionState.value === "connected") {
    send({ type: "unsubscribe", streams });
  }
}

export function resync(stream: WsStreamId, lastSeq?: number, params?: Record<string, unknown>, requestId?: string) {
  if (!socketRef.value || socketRef.value.readyState !== WebSocket.OPEN) {
    pendingResyncs.push({ stream, lastSeq, params, requestId });
    if (pendingResyncs.length > 64) {
      pendingResyncs.splice(0, pendingResyncs.length - 64);
    }
    return;
  }
  send({ type: "resync", stream, lastSeq, params, requestId });
}

function createRequestId(): string {
  const cryptoAny = crypto as unknown as { randomUUID?: () => string } | undefined;
  if (cryptoAny?.randomUUID) return cryptoAny.randomUUID();
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function sendAction<TKind extends WsActionKind>({
  kind,
  payload,
  clientRequestId,
  timeoutMs
}: {
  kind: TKind;
  payload: WsActionPayloadByKind[TKind];
  clientRequestId?: string;
  timeoutMs?: number;
}): Promise<WsActionAckMessage> {
  const ws = socketRef.value;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    return Promise.reject(new Error("WebSocket disconnected"));
  }

  const resolvedClientRequestId = clientRequestId ?? createRequestId();
  if (pendingActions.has(resolvedClientRequestId)) {
    return Promise.reject(new Error("Client request already pending"));
  }

  const actionTimeoutMs = Math.max(250, Math.floor(Number(timeoutMs ?? 10000)));
  return new Promise((resolve, reject) => {
    const request: PendingActionRequest = {
      clientRequestId: resolvedClientRequestId,
      kind,
      resolve,
      reject,
      timeoutId: null
    };
    request.timeoutId = setTimeoutCompat(() => {
      pendingActions.delete(resolvedClientRequestId);
      reject(new Error("Timed out waiting for action ack"));
    }, actionTimeoutMs);
    pendingActions.set(resolvedClientRequestId, request);

    send({ type: "action", clientRequestId: resolvedClientRequestId, kind, payload });
  });
}

export function requestSnapshot<TStream extends WsStreamId>(
  stream: TStream,
  params?: Record<string, unknown>,
  options?: { timeoutMs?: number }
) {
  const timeoutMs = Math.max(250, Math.floor(Number(options?.timeoutMs ?? 10000)));
  return new Promise<WsSnapshotMessage<TStream>>((resolve, reject) => {
    const requestId = createRequestId();
    let map = pendingSnapshots.get(stream);
    if (!map) {
      map = new Map();
      pendingSnapshots.set(stream, map);
    }
    const request: PendingSnapshotRequest = {
      requestId,
      resolve: resolve as (msg: WsSnapshotMessage<WsStreamId>) => void,
      reject,
      timeoutId: null
    };
    request.timeoutId = setTimeoutCompat(() => {
      map?.delete(requestId);
      if (map && map.size === 0) {
        pendingSnapshots.delete(stream);
      }
      reject(new Error(`Timed out waiting for ${stream} snapshot`));
    }, timeoutMs);
    map.set(requestId, request);

    resync(stream, lastSeqByStream.get(stream), params, requestId);
  });
}

export function onSnapshot<TStream extends WsStreamId>(stream: TStream, handler: SnapshotHandler<TStream>) {
  let set = snapshotHandlers.get(stream);
  if (!set) {
    set = new Set();
    snapshotHandlers.set(stream, set);
  }
  set.add(handler as SnapshotHandler<WsStreamId>);
  return () => set?.delete(handler as SnapshotHandler<WsStreamId>);
}

export function onEvent<TStream extends WsStreamId>(stream: TStream, handler: EventHandler<TStream>) {
  let set = eventHandlers.get(stream);
  if (!set) {
    set = new Set();
    eventHandlers.set(stream, set);
  }
  set.add(handler as EventHandler<WsStreamId>);
  return () => set?.delete(handler as EventHandler<WsStreamId>);
}

export function useSessionSocket() {
  const isConnected = computed(() => connectionState.value === "connected");
  const state = computed(() => connectionState.value);
  return {
    connect,
    disconnect,
    sendAction,
    subscribe,
    unsubscribe,
    resync,
    requestSnapshot,
    onSnapshot,
    onEvent,
    connectionState: state,
    isConnected,
    lastError,
    lastMessageAt
  };
}
